/**
 * Page de maintenance, affichee a la place du formulaire de connexion.
 *
 * 17/09/2026 (demande du club : "possibilite d'afficher la page de maintenance pendant les TNR
 * plutot que la page de login"). Le TNR dure ~4 minutes, bascule l'app_mode en test par moments
 * et cree/supprime des fiches : un adherent qui se connecte pendant ce temps voit des donnees
 * incoherentes sans comprendre pourquoi. Cette page le lui dit.
 *
 * Portee strictement visuelle : l'API reste ouverte. C'est deliberé - bloquer l'authentification
 * empecherait le TNR lui-meme de se connecter, or c'est justement pendant qu'il tourne que la
 * page doit s'afficher. Le bouton "Je suis administrateur" revele le formulaire : sans cette
 * sortie de secours, un oubli de desactivation fermerait le backoffice a tout le monde, y compris
 * a celui qui doit le rouvrir.
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Wrench, Waves } from 'lucide-react';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const MESSAGE_PAR_DEFAUT = "Une maintenance technique est en cours. L'application sera de nouveau "
  + "accessible dans quelques minutes.";

const MaintenancePage = ({ clubInfo = {}, onBypass, version }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-50 via-cyan-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-4">
    <div className="w-full max-w-md space-y-6">
      <div className="text-center">
        {clubInfo.logo_url ? (
          <img
            src={`${BACKEND_URL}${clubInfo.logo_url}`}
            alt={clubInfo.club_name || 'Logo du club'}
            className="w-20 h-20 mx-auto rounded-2xl object-contain mb-4"
          />
        ) : (
          <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-600 flex items-center justify-center shadow-lg shadow-sky-500/30 mb-4">
            <Waves className="w-10 h-10 text-white" />
          </div>
        )}
        <h1 className="text-2xl font-bold text-foreground">
          {clubInfo.app_title || clubInfo.club_name || 'CESAM Plongee'}
        </h1>
      </div>

      <Card className="border-amber-200 dark:border-amber-900">
        <CardHeader className="flex flex-row items-center gap-3 space-y-0">
          <div className="w-11 h-11 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0">
            <Wrench className="w-5 h-5 text-amber-700 dark:text-amber-400" />
          </div>
          <CardTitle className="text-lg">Maintenance en cours</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-muted-foreground leading-relaxed" data-testid="maintenance-message">
            {clubInfo.maintenance_message || MESSAGE_PAR_DEFAUT}
          </p>
          <p className="text-xs text-muted-foreground">
            Inutile de réessayer immédiatement : rechargez la page dans quelques minutes.
          </p>
          {onBypass && (
            <button
              type="button"
              onClick={onBypass}
              className="text-xs text-muted-foreground hover:text-primary underline underline-offset-2"
              data-testid="maintenance-bypass"
            >
              Je suis administrateur
            </button>
          )}
        </CardContent>
      </Card>

      {version && <p className="text-center text-xs text-muted-foreground">{version}</p>}
    </div>
  </div>
);

export default MaintenancePage;
